'use client';

import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { trackAppEvent } from '@/lib/posthog';

interface SignInProps {
  onStartExit: () => void;
}

/**
 * Auth0-based SignIn component.
 * Replaces legacy NextAuth signIn("cognito") flow.
 * Simply redirects to /auth/login (handled by Auth0 SDK / middleware).
 */
export default function SignIn({ onStartExit }: SignInProps) {
  const { notifyError } = useToast();
  const { login } = useAuth();

  const handleClick = async () => {
    trackAppEvent('login_initiated', { source: 'login_page' });
    onStartExit();
    // Preserve exit animation timing
    await new Promise((r) => setTimeout(r, 400));
    try {
      // Redirect to Auth0 universal login via replace() so /login is removed
      // from the browser history (prevents back-button loops).
      // The login() helper already uses window.location.replace() internally.
      login('/dashboard');
    } catch (error) {
      console.error('[SignIn] Auth0 login redirect failed:', error);
      trackAppEvent('login_failed', {
        error_code: error instanceof Error ? error.name : 'unknown',
        stage: 'auth0_callback',
      });
      notifyError('Authentication failed. Please try again.');
    }
  };

  return (
    <button
      className="focus:ring-purple-300px-5 h-[50px] w-full rounded-[5px] bg-[#522e90] py-2.5 text-center text-sm font-medium text-white hover:bg-purple-700 focus:ring-4 disabled:opacity-75 dark:bg-[#522e90] dark:hover:bg-purple-700 dark:focus:ring-purple-800"
      onClick={handleClick}
      type="button"
    >
      Sign in to Dream Connect
    </button>
  );
}

export const SignInButton = () => {
  const router = useRouter();
  return (
    <button
      className="ml-2 rounded-lg bg-purple-700 px-4 py-2 text-sm font-medium text-white hover:bg-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-600 dark:hover:bg-purple-700 dark:focus:ring-purple-800"
      onClick={() => {
        trackAppEvent('login_initiated', { source: 'login_page' });
        router.push('/login');
      }}
      type="button"
    >
      Sign In
    </button>
  );
};
